import { config } from 'config';
import { EmbeddedAppPathname } from 'lib/enum/embedded-pathname.enum';

export enum AppMode {
    Normal = 'normal',
    Embedded = 'embedded',
}

export enum TokenPrefix {
    Bearer = 'Bearer',
    Partnership = 'Partnership',
}

export enum PartnerName {
    Jobylon = 'jobylon',
    Otys = 'otys',
}

export enum EmbeddedAppSubdomain {
    Production = 'embedded-app',
    Staging = 'staging-embedded-app',
}

export enum EmbeddedAppHostname {
    Production = '.embedded-app.mrwork.',
    Staging = 'staging-embedded-app.mrwork.',
}

export interface EmbeddedAppConfig {
    partnerName: PartnerName | null;
    token: string | null;
    tokenPrefix: TokenPrefix;
}

export const isEmbeddedApp = (): boolean => {
    const hostname = window.location.hostname;
    return hostname.includes(EmbeddedAppHostname.Production) || hostname.includes(EmbeddedAppHostname.Staging);
};

export const getPartnerName = (): PartnerName | null => {
    if (!isEmbeddedApp()) {
        return null;
    }

    const hostname = window.location.hostname;
    const parts = hostname.split('.');

    const embeddedAppIndex = parts.findIndex(
        part => part === EmbeddedAppSubdomain.Production || part === EmbeddedAppSubdomain.Staging,
    );
    if (embeddedAppIndex > 0) {
        const partnerNameString = parts[embeddedAppIndex - 1];
        if (partnerNameString === PartnerName.Jobylon) {
            return PartnerName.Jobylon;
        }
        if (partnerNameString === PartnerName.Otys) {
            return PartnerName.Otys;
        }
    }

    return null;
};

export const getTokenFromUrl = (): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('token');
};

export const getTokenPrefix = (): TokenPrefix => {
    if (isEmbeddedApp()) {
        return TokenPrefix.Partnership;
    }
    return TokenPrefix.Bearer;
};

export const getEmbeddedAppConfig = (): EmbeddedAppConfig | null => {
    if (!isEmbeddedApp()) {
        return null;
    }

    const partnerName = getPartnerName();
    const token = getTokenFromUrl();
    const tokenPrefix = getTokenPrefix();

    if (!partnerName) {
        return null;
    }

    return {
        partnerName,
        token,
        tokenPrefix,
    };
};

export const getAppMode = (): AppMode => {
    return isEmbeddedApp() ? AppMode.Embedded : AppMode.Normal;
};

const getOriginFromUrl = (url: string): string | null => {
    try {
        return new URL(url).origin;
    } catch {
        return null;
    }
};

export const isDemoEmbeddedAppHost = (): boolean => {
    if (!isEmbeddedApp()) {
        return false;
    }

    const demoEmbeddedAppOrigin = getOriginFromUrl(config.demoEmbeddedAppUrl);

    return demoEmbeddedAppOrigin !== null && window.location.origin === demoEmbeddedAppOrigin;
};

export enum IframeContext {
    Inbox = 'inbox',
    Credentials = 'credentials',
    Settings = 'settings',
    TalentCrm = 'talent-crm',
}

const IFRAME_CONTEXT_STORAGE_KEY = 'embedded_iframe_context';

const storeIframeContext = (context: IframeContext): void => {
    sessionStorage.setItem(IFRAME_CONTEXT_STORAGE_KEY, context);
};

const normalizeEmbeddedPathname = (pathname: string): string => pathname.replace(/\/+$/, '') || '/';

const getStoredIframeContext = (): IframeContext | null => {
    const stored = sessionStorage.getItem(IFRAME_CONTEXT_STORAGE_KEY);
    if (stored === IframeContext.Credentials) return IframeContext.Credentials;
    if (stored === IframeContext.Settings) return IframeContext.Settings;
    if (stored === IframeContext.Inbox) return IframeContext.Inbox;
    if (stored === IframeContext.TalentCrm) return IframeContext.TalentCrm;

    return null;
};

export const getIframeContextFromPathname = ({
    pathname,
    partnerName,
    jobylonApplicationId,
    otysCandidateOuid,
    storedContext,
    isDemoEmbeddedAppHost = false,
}: {
    pathname: string;
    partnerName: PartnerName | null;
    jobylonApplicationId: string | null;
    otysCandidateOuid: string | null;
    storedContext: IframeContext | null;
    isDemoEmbeddedAppHost?: boolean;
}): IframeContext | null => {
    const normalizedPathname = normalizeEmbeddedPathname(pathname);
    const supportsTalentCrmContext =
        partnerName === PartnerName.Jobylon || partnerName === null || isDemoEmbeddedAppHost;

    if (normalizedPathname === EmbeddedAppPathname.Settings) {
        return IframeContext.Settings;
    }

    if (partnerName === PartnerName.Otys && normalizedPathname === EmbeddedAppPathname.Credentials) {
        return IframeContext.Credentials;
    }

    if (supportsTalentCrmContext && normalizedPathname === EmbeddedAppPathname.TalentCrm) {
        return IframeContext.TalentCrm;
    }

    if (normalizedPathname === EmbeddedAppPathname.Dashboard) {
        return IframeContext.Inbox;
    }

    if (normalizedPathname.includes('/otys/credentials')) return IframeContext.Credentials;
    if (normalizedPathname.includes('/settings')) return IframeContext.Settings;
    if (normalizedPathname.includes('/inbox')) return IframeContext.Inbox;
    if (jobylonApplicationId) return IframeContext.Inbox;
    if (otysCandidateOuid) return IframeContext.Inbox;
    if (storedContext === IframeContext.TalentCrm && !supportsTalentCrmContext) {
    } else if (storedContext !== null) {
        return storedContext;
    }
    if (normalizedPathname.startsWith('/b/')) return IframeContext.Inbox;

    return null;
};

export const getOrRestoreIframeContext = (): IframeContext | null => {
    if (!isEmbeddedApp()) {
        return null;
    }

    const pathname = normalizeEmbeddedPathname(window.location.pathname);
    const partnerName = getPartnerName();
    const demoEmbeddedAppHost = isDemoEmbeddedAppHost();
    const context = getIframeContextFromPathname({
        pathname,
        partnerName,
        jobylonApplicationId: getJobylonApplicationId(),
        otysCandidateOuid: getOtysCandidateOuid(),
        storedContext: getStoredIframeContext(),
        isDemoEmbeddedAppHost: demoEmbeddedAppHost,
    });

    if (
        context !== null &&
        (pathname === EmbeddedAppPathname.Settings ||
            pathname === EmbeddedAppPathname.Dashboard ||
            pathname === EmbeddedAppPathname.TalentCrm ||
            (partnerName === PartnerName.Otys && pathname === EmbeddedAppPathname.Credentials))
    ) {
        storeIframeContext(context);
    }

    return context;
};

export const isIframe = (): boolean => getOrRestoreIframeContext() !== null;

export const isJobylonTalentCrmIframe = ({
    appMode,
    embeddedConfig,
    iframeContext,
}: {
    appMode: AppMode;
    embeddedConfig: EmbeddedAppConfig | null;
    iframeContext: IframeContext | null;
}): boolean =>
    appMode === AppMode.Embedded &&
    (embeddedConfig?.partnerName === PartnerName.Jobylon || embeddedConfig === null) &&
    iframeContext === IframeContext.TalentCrm;

export const getJobylonApplicationId = (): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('application_id');
};

export const getOtysCandidateOuid = (): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('candidate_ouid') || urlParams.get('ex_ouid');
};
